import { supplementalFacilityAnswers } from './state-supplemental-answers.ts';
import type { TierIIFacilityInfo } from '../types/facility-info.ts';
import type { FacilityMeta, FacilityMetaContact } from './tier2submit-export.ts';
import type { StateDocumentStatus } from './state-fields.ts';
/** Shared facility/contact projection for draft and submitted exports. */
export function tierIIFacilityMeta(
  info: TierIIFacilityInfo,
  facilityID: number,
  reportingYear: number,
  documentStatus: StateDocumentStatus,
): FacilityMeta {
  const contacts: FacilityMetaContact[] = [];
  const answers = supplementalFacilityAnswers(info.stateReporting, info.facility.state);
  const ownerPhone24 = answers.ownerOperatorPhone24;
  const parentPhone = answers.parentCompanyPhone;
  if (info.facility.technicalContactName.trim()) {
    contacts.push({
      recordKey: 'owner-operator',
      name: info.facility.technicalContactName,
      nameIsBusiness: true,
      title: null,
      email: info.facility.technicalContactEmail,
      parentCompanyDunAndBradstreet: null,
      contactTypes: ['Owner / Operator'],
      mailingAddress: {
        street: info.facility.address,
        city: info.facility.city,
        state: info.facility.state,
        zip: info.facility.zip,
        country: 'USA',
      },
      phones: [
        ...(info.facility.technicalContactPhone
          ? [{ number: info.facility.technicalContactPhone, type: 'Work' as const }]
          : []),
        ...(ownerPhone24?.trim() ? [{ number: ownerPhone24, type: '24-hour' as const }] : []),
      ],
    });
  }
  if (info.facility.parentCompanyName.trim()) {
    contacts.push({
      recordKey: 'parent-company',
      name: info.facility.parentCompanyName,
      nameIsBusiness: true,
      title: null,
      email: null,
      parentCompanyDunAndBradstreet: info.facility.parentCompanyDunBradstreetCode,
      contactTypes: ['Parent Company'],
      mailingAddress: null,
      phones: parentPhone?.trim() ? [{ number: parentPhone, type: 'Work' }] : [],
    });
  }
  if (info.tierIIContactName.trim()) {
    contacts.push({
      recordKey: 'tierii-contact',
      name: info.tierIIContactName,
      nameIsBusiness: false,
      title: info.tierIIContactTitle,
      email: info.tierIIContactEmail,
      parentCompanyDunAndBradstreet: null,
      contactTypes: ['Tier II Information Contact'],
      mailingAddress: null,
      phones: info.tierIIContactPhone ? [{ number: info.tierIIContactPhone, type: 'Work' }] : [],
    });
  }
  // Every row is kept, even unnamed ones, so the report checks can flag incomplete contacts
  // rather than the export silently dropping them.
  contacts.push(
    ...info.emergencyContacts.map(
      (contact): FacilityMetaContact => ({
        recordKey: contact.id,
        name: contact.name,
        nameIsBusiness: false,
        title: contact.title,
        email: contact.email,
        parentCompanyDunAndBradstreet: null,
        contactTypes: [
          'Emergency Contact' as const,
          ...(contact.isFacilityEmergencyCoordinator ? ['Fac. Emergency Coordinator' as const] : []),
          ...(contact.isEmergencyEquipmentContact ? ['Emergency Equipment Contact' as const] : []),
        ],
        mailingAddress: null,
        phones: [
          ...(contact.phone ? [{ number: contact.phone, type: 'Emergency' as const }] : []),
          ...(contact.phone24 ? [{ number: contact.phone24, type: '24-hour' as const }] : []),
        ],
      }),
    ),
  );

  return {
    stateReporting: info.stateReporting,
    jurisdictionProfile: info.jurisdictionProfile,
    reportingYear,
    documentStatus,
    facilityID: facilityID,
    name: info.facility.properName || info.facility.name,
    address: info.facility.address,
    city: info.facility.city,
    county: info.stateReporting.facilityOverrides[info.facility.state]?.county ?? info.facility.county,
    state: info.facility.state,
    zip: info.facility.zip,
    mailingAddress: info.facility.mailingAddress,
    mailingCity: info.facility.mailingCity,
    mailingState: info.facility.mailingState,
    mailingZip: info.facility.mailingZip,
    fireDistrict: info.fireDistrict,
    department: info.department,
    latitude: info.latitude,
    longitude: info.longitude,
    emergencyPhone: info.emergencyPhone,
    rmpFacilityID: info.rmpFacilityID,
    certifierName: info.facility.certifierName,
    certifierTitle: info.facility.certifierTitle,
    dateSigned: info.dateSigned,
    naicsCodes: info.facility.naicsCodes,
    dunBradstreetCodes: info.facility.dunBradstreetCodes,
    triID: info.facility.triID,
    occupancyStatus: info.occupancyStatus,
    maxOccupants: info.maxOccupants,
    subjectToSection302: info.subjectToSection302,
    subjectToSection112r: info.subjectToSection112r,
    contacts,
  };
}
